import {
    ArrowLeft,
    BarChart3,
    Clock,
    Download,
    Globe,
    Info,
    MousePointerClick,
    Share2,
    Smartphone,
    Users
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getUrlStats } from '../api/urlService';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.tsx';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog.tsx';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table.tsx';

interface UrlStatsResponse {
  url: {
    id: string;
    originalUrl: string;
    shortCode: string;
    shortUrl: string;
    qrCodeUrl: string;
    visitCount: number;
    createdAt: string;
  };
  visitsByDate: Array<{
    date: string;
    count: number;
  }>;
  topBrowsers: Array<{
    browser: string;
    count: number;
  }>;
  topOS: Array<{
    os: string;
    count: number;
  }>;
  topDevices: Array<{
    deviceType: string;
    count: number;
  }>;
  detailedVisits: Array<{
    id: string;
    timestamp: string;
    browser: string;
    os: string;
    deviceType: string;
    language: string;
    referrer: string;
    ipAddress: string;
    extended?: any;
  }>;
}

const StatCard = ({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  description?: string;
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </CardContent>
  </Card>
);

const UrlStats = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [stats, setStats] = useState<UrlStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVisit, setSelectedVisit] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      if (!shortCode) return;

      try {
        setLoading(true);
        const data = await getUrlStats(shortCode);
        setStats(data);
      } catch (err) {
        setError('Failed to fetch URL statistics');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [shortCode]);

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate average visits per day
  const calculateAverageVisitsPerDay = () => {
    if (!stats || !stats.url || !stats.visitsByDate || stats.visitsByDate.length === 0) {
      return 0;
    }

    // Calculate days since URL creation
    const creationDate = new Date(stats.url.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - creationDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Use at least 1 day to avoid division by zero
    const days = Math.max(1, diffDays);

    return stats.url.visitCount / days;
  };

  // Get percentage of mobile visits
  const getMobilePercentage = () => {
    if (!stats || !stats.url || !stats.topDevices) {
      return 0;
    }

    const mobileDevice = stats.topDevices.find(device => device.deviceType.toLowerCase() === 'mobile');
    if (!mobileDevice || stats.url.visitCount === 0) {
      return 0;
    }

    return (mobileDevice.count / stats.url.visitCount) * 100;
  };

  // Get unique visitors count (estimate based on IP addresses)
  const getUniqueVisitors = () => {
    if (!stats || !stats.detailedVisits) {
      return 0;
    }

    const uniqueIPs = new Set();
    stats.detailedVisits.forEach(visit => {
      if (visit.ipAddress) {
        uniqueIPs.add(visit.ipAddress);
      }
    });

    return uniqueIPs.size;
  };

  const showVisitDetails = (visit: any) => {
    setSelectedVisit(visit);
    setDetailsOpen(true);
  };

  // Format JSON data for display
  const formatJson = (jsonObj: any) => {
    return JSON.stringify(jsonObj, null, 2);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-4">
        <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
          {error || 'Failed to load statistics'}
        </div>
      </div>
    );
  }

  const averageVisitsPerDay = calculateAverageVisitsPerDay();
  const mobilePercentage = getMobilePercentage();
  const uniqueVisitors = getUniqueVisitors();

  return (
    <div className="container py-6">
      <div className="flex flex-col gap-6">
        {/* Back navigation */}
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-4">
            <Link to="/urls" className="flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" />
              BACK TO HOME
            </Link>
          </Button>

          <h1 className="text-3xl font-bold tracking-tight">URL Statistics & QR Code</h1>

          {/* URL information */}
          <div className="mt-4 space-y-2">
            <div>
              <div className="text-sm text-muted-foreground">Original URL</div>
              <div className="text-sm font-medium break-all">{stats.url.originalUrl}</div>
            </div>

            <div>
              <div className="text-sm text-muted-foreground">Short URL</div>
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium">{stats.url.shortUrl}</div>
                <Button variant="outline" size="sm">COPY</Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="text-sm">
                <span className="text-muted-foreground">Created: </span>
                <span>{new Date(stats.url.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <MousePointerClick className="h-3 w-3" />
                {stats.url.visitCount} visits
              </div>
            </div>
          </div>
        </div>

        {/* QR Code Card */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-2">
              <StatCard
                title="Total Visits"
                value={stats.url.visitCount}
                icon={MousePointerClick}
                description="All-time total clicks"
              />
              <StatCard
                title="Unique Visitors"
                value={uniqueVisitors}
                icon={Users}
                description="Individual visitors"
              />
              <StatCard
                title="Average Daily Visits"
                value={averageVisitsPerDay.toFixed(1)}
                icon={BarChart3}
                description="Clicks per day"
              />
              <StatCard
                title="Mobile Traffic"
                value={`${mobilePercentage.toFixed(1)}%`}
                icon={Smartphone}
                description="Visits from mobile devices"
              />
            </div>

            {/* Browser and Device Distribution */}
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Top Browsers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.topBrowsers && stats.topBrowsers.map((browser) => (
                      <div
                        key={browser.browser}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm font-medium">{browser.browser || 'Unknown'}</span>
                        <span className="text-sm text-muted-foreground">
                          {browser.count} visits
                        </span>
                      </div>
                    ))}
                    {(!stats.topBrowsers || stats.topBrowsers.length === 0) && (
                      <div className="text-sm text-muted-foreground">No browser data available</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Device Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {stats.topDevices && stats.topDevices.map((device) => (
                      <div
                        key={device.deviceType}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm font-medium">{device.deviceType || 'Unknown'}</span>
                        <span className="text-sm text-muted-foreground">
                          {device.count} visits
                        </span>
                      </div>
                    ))}
                    {(!stats.topDevices || stats.topDevices.length === 0) && (
                      <div className="text-sm text-muted-foreground">No device data available</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* QR Code */}
          <Card>
            <CardHeader>
              <CardTitle>QR Code for this Short URL</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-4">
              <div className="p-4 border rounded-md">
                <img src={stats.url.qrCodeUrl} alt="QR Code" className="w-48 h-48" />
              </div>
              <Button variant="outline" className="w-full">
                <Download className="mr-2 h-4 w-4" />
                DOWNLOAD QR CODE
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Dashboard */}
        <Card>
          <CardHeader>
            <CardTitle>Analytics Dashboard</CardTitle>
            <div className="flex justify-end gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm">Date Range:</span>
                <select className="border rounded p-1 text-sm">
                  <option>All Time</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Chart Size:</span>
                <select className="border rounded p-1 text-sm">
                  <option>Medium</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-48 w-full flex justify-center items-center border-b">
              <div className="text-muted-foreground">Visits over time visualization will appear here</div>
            </div>

            <div className="flex justify-center gap-4 py-4">
              <Button variant="ghost" className="text-xs" size="sm">VISITS OVER TIME</Button>
              <Button variant="ghost" className="text-xs" size="sm">BROWSERS</Button>
              <Button variant="ghost" className="text-xs" size="sm">OPERATING SYSTEMS</Button>
              <Button variant="ghost" className="text-xs" size="sm">DEVICE TYPES</Button>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Visit Data */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Detailed Visit Data
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                EXPORT CSV
              </Button>
              <Button variant="outline" size="sm">HIDE DETAILS</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground mb-4">
              This table shows detailed information about each visit, including client-side fingerprinting data when available.
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Browser</TableHead>
                    <TableHead>OS</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Referrer</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.detailedVisits && stats.detailedVisits.map((visit) => (
                    <TableRow key={visit.id}>
                      <TableCell>{formatDate(visit.timestamp)}</TableCell>
                      <TableCell>{visit.browser || 'Unknown'}</TableCell>
                      <TableCell>{visit.os || 'Unknown'}</TableCell>
                      <TableCell>{visit.deviceType || 'Unknown'}</TableCell>
                      <TableCell>{visit.referrer || 'Direct'}</TableCell>
                      <TableCell>{visit.ipAddress || '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-0 h-auto"
                          onClick={() => showVisitDetails(visit)}
                        >
                          <span className="text-primary">Details</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!stats.detailedVisits || stats.detailedVisits.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-4">
                        No visit data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visit Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Visit Details
            </DialogTitle>
            <DialogDescription>
              Detailed information about the visitor including device, browser, and fingerprinting data
            </DialogDescription>
          </DialogHeader>

          {selectedVisit && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Basic Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-1">
                        <div className="font-medium">Timestamp:</div>
                        <div>{formatDate(selectedVisit.timestamp)}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="font-medium">Browser:</div>
                        <div>{selectedVisit.browser || 'Unknown'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="font-medium">Operating System:</div>
                        <div>{selectedVisit.os || 'Unknown'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="font-medium">Device Type:</div>
                        <div>{selectedVisit.deviceType || 'Unknown'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="font-medium">Language:</div>
                        <div>{selectedVisit.language || 'Unknown'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="font-medium">Referrer:</div>
                        <div>{selectedVisit.referrer || 'Direct'}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <div className="font-medium">IP Address:</div>
                        <div>{selectedVisit.ipAddress || '-'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {selectedVisit.extended && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Extended Data</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        {selectedVisit.extended.serverData && (
                          <div>
                            <div className="font-medium mb-1">Server Data:</div>
                            <div className="p-2 bg-muted rounded-md overflow-x-auto">
                              <pre className="text-xs">{formatJson(selectedVisit.extended.serverData)}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Additional extended data sections */}
              {selectedVisit.extended && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedVisit.extended.features && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Browser Features</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="p-2 bg-muted rounded-md overflow-x-auto">
                          <pre className="text-xs">{formatJson(selectedVisit.extended.features)}</pre>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {selectedVisit.extended.screenInfo && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Screen Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="p-2 bg-muted rounded-md overflow-x-auto">
                          <pre className="text-xs">{formatJson(selectedVisit.extended.screenInfo)}</pre>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <DialogClose asChild>
                  <Button type="button">Close</Button>
                </DialogClose>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UrlStats;
